import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { SubmitBankKycArgs } from '@share/interfaces';
import { toast } from 'react-toastify';
import { useAPI } from 'src/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { user } from '@state/user';
import Spinner from '@com/utils/Spinner';
import _countryList from '../assets/i18n/countries.json';
import loadable from '@loadable/component';

const ErrorMsg = loadable(() => import('@com/utils/ErrorMsg'));
const Footer = loadable(() => import('@com/footer/Footer'));
const Header = loadable(() => import('@com/header/Header'));
const TopSection = loadable(() => import('@com/TopSection'));

function BankKYC(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userData, language } = user.useContainer();
  const [selectedFile, setSelectedFile] = useState<File>();
  const [fileImage, setFileImage] = useState<string>(
    t('editProfilePage.noFile')
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { submitBankKyc } = useAPI();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<SubmitBankKycArgs>();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e?.target?.files;
    if (files) {
      if (files[0].size > 2048000) {
        toast.error(t('toastMessage.imageFileSizeMax2MB'));
        return;
      }
      setSelectedFile(files[0]);
      setFileImage(files[0].name);
    }
  };

  const onSubmit = useDebouncedCallback(
    async (data: SubmitBankKycArgs) => {
      setIsLoading(true);
      const hasResponse = await submitBankKyc({
        country: data.country,
        bankName: data.bankName,
        bankAccountNumber: data.bankAccountNumber,
        cardImage: selectedFile!,
      });

      if (hasResponse) {
        toast.success(t('editProfilePage.kycSubmited'));
        navigate('/edit-profile?type=auth');
      }
      setIsLoading(false);
    },
    500,
    { leading: true, trailing: false }
  );

  const countryList = useMemo(() => {
    return _countryList[language];
  }, [language]);

  useEffect(() => {
    // FIXME: If admin deleted approval, user have to relogin to access this page
    if (userData?.user?.bank_kyc) {
      navigate('/edit-profile?type=auth');
    }
  }, []);

  return (
    <>
      <Header />
      <TopSection sectionName={t('editProfilePage.bankKYC')} />
      <section className="content-container tf-login tf-section">
        <div className="themesflat-container" style={{ minHeight: '45vh' }}>
          <div className="row">
            <div className="col-12">
              <div className="flat-form box-login-email">
                <div className="form-inner">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <select
                      {...register('country', {
                        required: {
                          value: true,
                          message: t('errorMsg.editProfile.countryRequired'),
                        },
                      })}
                      onChange={(e) =>
                        setValue('country', e.target.value, {
                          shouldValidate: true,
                        })
                      }
                    >
                      <option value="">
                        {t('editProfilePage.yourCountry')}
                      </option>
                      {countryList?.map((item) => (
                        <option key={item.countryName} value={item.countryCode}>
                          {item.countryName}
                        </option>
                      ))}
                    </select>
                    <ErrorMsg errors={errors} name="country" />
                    <input
                      aria-required
                      aria-label="bankName"
                      placeholder={t('editProfilePage.yourBankName')}
                      type="text"
                      {...register('bankName', {
                        required: {
                          value: true,
                          message: t('errorMsg.editProfile.bankNameRequired'),
                        },
                      })}
                    />
                    <ErrorMsg errors={errors} name="bankName" />
                    <input
                      aria-required
                      aria-label="bankAccountNumber"
                      placeholder={t('editProfilePage.yourAccountNumber')}
                      type="text"
                      {...register('bankAccountNumber', {
                        required: {
                          value: true,
                          message: t(
                            'errorMsg.editProfile.accountNumberRequired'
                          ),
                        },
                      })}
                    />
                    <ErrorMsg errors={errors} name="bankAccountNumber" />
                    <div className="row">
                      <h4 className="col-5 my-5 ml-2">
                        {t('editProfilePage.bankStatement')}
                      </h4>
                      <input
                        aria-required
                        accept="image/png, image/jpg, image/jpeg"
                        aria-label="cardImage"
                        className="col-4 mb-4 d-none"
                        id="file"
                        type="file"
                        {...register('cardImage', {
                          required: {
                            value: true,
                            message: t(
                              'errorMsg.editProfile.bankImageRequired'
                            ),
                          },
                        })}
                        onChange={handleChange}
                      />
                      <label className="col-5 m-auto" htmlFor="file">
                        {` ${t(
                          'editProfilePage.ChooseFile'
                        )} \r\n  ${fileImage}`}
                      </label>
                    </div>
                    <ErrorMsg errors={errors} name="cardImage" />
                    <button
                      className="mt-5 d-flex justify-content-center no-hover"
                      disabled={isLoading}
                      type="submit"
                    >
                      {t('editProfilePage.submitKYC')}
                      <Spinner className="ml-2" width={isLoading ? 24 : 0} />
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

export default BankKYC;
